import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Loader2, Send, Save, Mail, BookOpen, CheckCircle2, Clock, Camera, RotateCcw, CalendarDays, BarChart3, Eye, Radio } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fmtDateTime } from "@/lib/date";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const inputCls = "mt-1.5 bg-[#1E2A5A] border-white/10 focus-visible:ring-[#E6B34A]";

export default function DevotionManager() {
  const [dev, setDev] = useState(null);
  const [optin, setOptin] = useState(0);
  const [emailReady, setEmailReady] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [theme, setTheme] = useState("");
  const [authorPhoto, setAuthorPhoto] = useState("");
  const [stats, setStats] = useState(null);
  const photoRef = useRef(null);

  const loadStats = () => api.get("/devotions/stats").then((r) => setStats(r.data)).catch(() => {});
  const load = () => {
    setLoading(true);
    api.get("/devotions/today").then((r) => {
      setDev(r.data.devotion);
      setOptin(r.data.optin_count);
      setEmailReady(r.data.email_ready);
      setAuthorPhoto(r.data.author_photo || "");
    }).catch(() => {}).finally(() => setLoading(false));
    loadStats();
  };
  useEffect(() => { load(); }, []);

  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("photo");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/devotions/author-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAuthorPhoto(`${r.data.author_photo}&t=${Date.now()}`);
      toast.success("Signature photo updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload photo"); }
    finally { setBusy(null); }
  };

  const resetPhoto = async () => {
    setBusy("photo");
    try {
      const r = await api.delete("/devotions/author-photo");
      setAuthorPhoto(`${r.data.author_photo}?t=${Date.now()}`);
      toast.success("Reverted to the default photo");
    } catch (err) { toast.error("Could not reset photo"); }
    finally { setBusy(null); }
  };

  const set = (k, v) => setDev({ ...dev, [k]: v });

  const generate = async () => {
    setBusy("generate");
    try {
      const r = await api.post("/devotions/generate", { theme });
      setDev(r.data);
      toast.success("Devotion generated — review and edit before sending");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate devotion"); }
    finally { setBusy(null); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const r = await api.put("/devotions/today", {
        title: dev.title, verse_reference: dev.verse_reference, verse_text: dev.verse_text,
        reflection: dev.reflection, prayer: dev.prayer, theme: dev.theme,
      });
      setDev(r.data);
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setBusy(null); }
  };

  const sendTest = async () => {
    setBusy("test");
    try {
      const r = await api.post("/devotions/send", { test: true });
      toast.success(`Test devotion sent to your inbox (${r.data.recipients})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send test"); }
    finally { setBusy(null); }
  };

  const sendAll = async () => {
    setBusy("send");
    try {
      const r = await api.post("/devotions/send", {});
      toast.success(`Sending today's devotion to ${r.data.recipients} listeners 🙌`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    finally { setBusy(null); }
  };

  const sendDigest = async (test) => {
    setBusy(test ? "digest-test" : "digest");
    try {
      const r = await api.post("/devotions/digest/send", { test });
      toast.success(test ? `Test digest of ${r.data.devotions} devotions sent to you` : `Weekly digest sent to ${r.data.recipients} listeners 🎉`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send digest"); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="flex items-center gap-2 text-zinc-400 py-12"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;

  return (
    <div className="max-w-2xl" data-testid="devotion-manager">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display font-extrabold text-2xl tracking-tight flex items-center gap-2"><BookOpen className="w-6 h-6 text-[#E6B34A]" /> Devotion of the Day</h2>
          <p className="text-sm text-zinc-400 mt-1">AI writes a daily devotional, emailed to <span className="text-[#E6B34A] font-semibold" data-testid="devotion-optin-count">{optin}</span> opted-in listeners at 8:00 AM (US Eastern). Edit or send it yourself anytime.</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${dev?.status === "sent" ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-zinc-300"}`} data-testid="devotion-status">
          {dev?.status === "sent" ? <><CheckCircle2 className="w-3.5 h-3.5" /> Sent today · {dev.recipients}</> : <><Clock className="w-3.5 h-3.5" /> {dev ? "Draft ready" : "Not generated"}</>}
        </span>
      </div>

      {!emailReady && <div className="mb-4 text-sm text-[#FFB020] bg-[#FFB020]/10 border border-[#FFB020]/20 rounded-xl px-4 py-3">Email provider not configured — add a Resend API key to enable sending.</div>}

      <div className="flex items-center gap-4 mb-6 p-4 rounded-2xl bg-[#141C42] border border-white/5" data-testid="devotion-photo-card">
        <img src={authorPhoto} alt="Signature" data-testid="devotion-author-photo" className="w-16 h-16 rounded-full object-cover border-2 border-[#E6B34A] bg-white shrink-0" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Signature photo</p>
          <p className="text-xs text-zinc-400 mt-0.5">Shown beside “Blessings, David Porter” at the bottom of every devotion email.</p>
        </div>
        <input ref={photoRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,.png,.jpg,.jpeg,.webp,.gif" onChange={uploadPhoto} className="hidden" data-testid="devotion-photo-input" />
        <button onClick={() => photoRef.current?.click()} disabled={busy === "photo"} data-testid="devotion-photo-upload" className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 text-sm font-semibold transition-colors disabled:opacity-60 shrink-0">
          {busy === "photo" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} Change
        </button>
        <button onClick={resetPhoto} disabled={busy === "photo"} data-testid="devotion-photo-reset" title="Reset to default" className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors disabled:opacity-60 shrink-0"><RotateCcw className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6 p-4 rounded-2xl bg-[#141C42] border border-white/5">
        <div className="flex-1 min-w-[180px]">
          <label className="overline text-zinc-400">Theme (optional)</label>
          <Input value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="e.g. hope, gratitude, courage" data-testid="devotion-theme" className={inputCls} />
        </div>
        <button onClick={generate} disabled={busy === "generate"} data-testid="devotion-generate" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
          {busy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {dev ? "Regenerate" : "Generate with AI"}
        </button>
      </div>

      {dev && (
        <div className="space-y-4">
          <div><label className="overline text-zinc-400">Title</label><Input value={dev.title} onChange={(e) => set("title", e.target.value)} data-testid="devotion-title" className={inputCls} /></div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1"><label className="overline text-zinc-400">Verse reference</label><Input value={dev.verse_reference} onChange={(e) => set("verse_reference", e.target.value)} data-testid="devotion-verse-ref" className={inputCls} /></div>
            <div className="sm:col-span-2"><label className="overline text-zinc-400">Verse text</label><Input value={dev.verse_text} onChange={(e) => set("verse_text", e.target.value)} data-testid="devotion-verse-text" className={inputCls} /></div>
          </div>
          <div><label className="overline text-zinc-400">Reflection</label><Textarea value={dev.reflection} onChange={(e) => set("reflection", e.target.value)} rows={6} data-testid="devotion-reflection" className={inputCls} /></div>
          <div><label className="overline text-zinc-400">Prayer</label><Textarea value={dev.prayer} onChange={(e) => set("prayer", e.target.value)} rows={3} data-testid="devotion-prayer" className={inputCls} /></div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button onClick={save} disabled={busy === "save"} data-testid="devotion-save" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 font-semibold transition-colors disabled:opacity-60">
              {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
            </button>
            <button onClick={sendTest} disabled={busy === "test" || !emailReady} data-testid="devotion-send-test" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 font-semibold transition-colors disabled:opacity-60">
              {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test to me
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button disabled={busy === "send" || !emailReady} data-testid="devotion-send-all" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#E6B34A] text-[#0A1030] font-bold hover:bg-[#CF9A34] transition-colors disabled:opacity-60">
                  {busy === "send" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to {optin} listeners
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#141C42] border-white/10 text-white">
                <AlertDialogHeader>
                  <AlertDialogTitle>Send today's devotion now?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">This will email "{dev.title}" to {optin} opted-in listeners right away.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-white/5 border-white/10 hover:bg-white/10 text-white">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={sendAll} data-testid="devotion-send-confirm" className="bg-[#E6B34A] text-[#0A1030] hover:bg-[#CF9A34]">Send now</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
          {dev.status === "sent" && <p className="text-xs text-zinc-500">Last sent {dev.sent_at ? fmtDateTime(dev.sent_at) : ""} to {dev.recipients} listeners.</p>}
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-white/10" data-testid="devotion-digest">
        <h3 className="font-semibold flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#E6B34A]" /> Weekly digest</h3>
        <p className="text-sm text-zinc-400 mt-1 mb-3">A recap of this week's devotions, emailed automatically every Sunday at 5 PM ET to digest subscribers. Send it manually anytime.</p>
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={() => sendDigest(true)} disabled={busy === "digest-test" || !emailReady} data-testid="digest-send-test" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 font-semibold transition-colors disabled:opacity-60">
            {busy === "digest-test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Send test digest to me
          </button>
          <button onClick={() => sendDigest(false)} disabled={busy === "digest" || !emailReady} data-testid="digest-send-all" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-zinc-200 font-semibold transition-colors disabled:opacity-60">
            {busy === "digest" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send digest now
          </button>
        </div>
      </div>

      {stats && (stats.devotions.length > 0 || stats.digest) && (
        <div className="mt-8 pt-6 border-t border-white/10" data-testid="devotion-insights">
          <h3 className="font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#E6B34A]" /> Open-rate insights</h3>
          <p className="text-sm text-zinc-400 mt-1 mb-4">How many listeners opened each email and tapped “Listen Live” from it.</p>
          {stats.digest && (
            <div className="flex items-center gap-4 mb-4 p-3.5 rounded-xl bg-[#101735] border border-white/5" data-testid="insight-digest">
              <CalendarDays className="w-5 h-5 text-[#E6B34A] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Weekly digest · {stats.digest.week}</p>
                <p className="text-xs text-zinc-400">{stats.digest.opens} of {stats.digest.recipients} opened · {stats.digest.clicks || 0} tapped Listen Live</p>
              </div>
              <span className="text-lg font-bold text-[#E6B34A]">{stats.digest.open_rate}%</span>
            </div>
          )}
          {stats.devotions.length > 0 && (
            <div className="rounded-xl border border-white/5 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#101735] text-zinc-400">
                  <tr>
                    <th className="text-left font-medium px-4 py-2.5">Devotion</th>
                    <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap"><Eye className="w-3.5 h-3.5 inline -mt-0.5" /> Opens</th>
                    <th className="text-right font-medium px-4 py-2.5 whitespace-nowrap"><Radio className="w-3.5 h-3.5 inline -mt-0.5" /> Listen taps</th>
                    <th className="text-right font-medium px-4 py-2.5">Open rate</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.devotions.map((d) => (
                    <tr key={d.id} className="border-t border-white/5" data-testid={`insight-row-${d.id}`}>
                      <td className="px-4 py-2.5">
                        <p className="font-medium truncate max-w-[280px]">{d.title}</p>
                        <p className="text-xs text-zinc-500">{d.date}</p>
                      </td>
                      <td className="px-4 py-2.5 text-right text-zinc-300 whitespace-nowrap">{d.opens} / {d.recipients}</td>
                      <td className="px-4 py-2.5 text-right whitespace-nowrap" data-testid={`insight-clicks-${d.id}`}>
                        <span className="text-zinc-300">{d.clicks || 0}</span>
                        <span className="text-xs text-zinc-500"> · {d.click_rate || 0}%</span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className={`font-bold ${d.open_rate >= 40 ? "text-emerald-400" : d.open_rate >= 20 ? "text-[#E6B34A]" : "text-zinc-400"}`}>{d.open_rate}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-[11px] text-zinc-600 mt-2">Note: open tracking relies on the reader loading images; some clients block this, so real reach may be higher.</p>
        </div>
      )}
    </div>
  );
}
